/**
 * @license
 * Copyright 2019 Google LLC
 * SPDX-License-Identifier: BSD-3-Clause
 */
const xt = globalThis, Nt = xt.ShadowRoot && (xt.ShadyCSS === void 0 || xt.ShadyCSS.nativeShadow) && "adoptedStyleSheets" in Document.prototype && "replace" in CSSStyleSheet.prototype, pe = Symbol(), Kt = /* @__PURE__ */ new WeakMap();
let Te = class {
  constructor(t, i, n) {
    if (this._$cssResult$ = !0, n !== pe) throw Error("CSSResult is not constructable. Use `unsafeCSS` or `css` instead.");
    this.cssText = t, this.t = i;
  }
  get styleSheet() {
    let t = this.o;
    const i = this.t;
    if (Nt && t === void 0) {
      const n = i !== void 0 && i.length === 1;
      n && (t = Kt.get(i)), t === void 0 && ((this.o = t = new CSSStyleSheet()).replaceSync(this.cssText), n && Kt.set(i, t));
    }
    return t;
  }
  toString() {
    return this.cssText;
  }
};
const ue = (e) => new Te(typeof e == "string" ? e : e + "", void 0, pe), Re = (e, t) => {
  if (Nt) e.adoptedStyleSheets = t.map((i) => i instanceof CSSStyleSheet ? i : i.styleSheet);
  else for (const i of t) {
    const n = document.createElement("style"), o = xt.litNonce;
    o !== void 0 && n.setAttribute("nonce", o), n.textContent = i.cssText, e.appendChild(n);
  }
}, Gt = Nt ? (e) => e : (e) => e instanceof CSSStyleSheet ? ((t) => {
  let i = "";
  for (const n of t.cssRules) i += n.cssText;
  return ue(i);
})(e) : e;
/**
 * @license
 * Copyright 2017 Google LLC
 * SPDX-License-Identifier: BSD-3-Clause
 */
const { is: Le, defineProperty: He, getOwnPropertyDescriptor: Me, getOwnPropertyNames: Ue, getOwnPropertySymbols: De, getPrototypeOf: Ne } = Object, Ct = globalThis, Jt = Ct.trustedTypes, Ie = Jt ? Jt.emptyScript : "", ze = Ct.reactiveElementPolyfillSupport, ct = (e, t) => e, $t = { toAttribute(e, t) {
  switch (t) {
    case Boolean:
      e = e ? Ie : null;
      break;
    case Object:
    case Array:
      e = e == null ? e : JSON.stringify(e);
  }
  return e;
}, fromAttribute(e, t) {
  let i = e;
  switch (t) {
    case Boolean:
      i = e !== null;
      break;
    case Number:
      i = e === null ? null : Number(e);
      break;
    case Object:
    case Array:
      try {
        i = JSON.parse(e);
      } catch {
        i = null;
      }
  }
  return i;
} }, It = (e, t) => !Le(e, t), Zt = { attribute: !0, type: String, converter: $t, reflect: !1, useDefault: !1, hasChanged: It };
Symbol.metadata ??= Symbol("metadata"), Ct.litPropertyMetadata ??= /* @__PURE__ */ new WeakMap();
let J = class extends HTMLElement {
  static addInitializer(t) {
    this._$Ei(), (this.l ??= []).push(t);
  }
  static get observedAttributes() {
    return this.finalize(), this._$Eh && [...this._$Eh.keys()];
  }
  static createProperty(t, i = Zt) {
    if (i.state && (i.attribute = !1), this._$Ei(), this.prototype.hasOwnProperty(t) && ((i = Object.create(i)).wrapped = !0), this.elementProperties.set(t, i), !i.noAccessor) {
      const n = Symbol(), o = this.getPropertyDescriptor(t, n, i);
      o !== void 0 && He(this.prototype, t, o);
    }
  }
  static getPropertyDescriptor(t, i, n) {
    const { get: o, set: r } = Me(this.prototype, t) ?? { get() {
      return this[i];
    }, set(s) {
      this[i] = s;
    } };
    return { get: o, set(s) {
      const l = o?.call(this);
      r?.call(this, s), this.requestUpdate(t, l, n);
    }, configurable: !0, enumerable: !0 };
  }
  static getPropertyOptions(t) {
    return this.elementProperties.get(t) ?? Zt;
  }
  static _$Ei() {
    if (this.hasOwnProperty(ct("elementProperties"))) return;
    const t = Ne(this);
    t.finalize(), t.l !== void 0 && (this.l = [...t.l]), this.elementProperties = new Map(t.elementProperties);
  }
  static finalize() {
    if (this.hasOwnProperty(ct("finalized"))) return;
    if (this.finalized = !0, this._$Ei(), this.hasOwnProperty(ct("properties"))) {
      const i = this.properties, n = [...Ue(i), ...De(i)];
      for (const o of n) this.createProperty(o, i[o]);
    }
    const t = this[Symbol.metadata];
    if (t !== null) {
      const i = litPropertyMetadata.get(t);
      if (i !== void 0) for (const [n, o] of i) this.elementProperties.set(n, o);
    }
    this._$Eh = /* @__PURE__ */ new Map();
    for (const [i, n] of this.elementProperties) {
      const o = this._$Eu(i, n);
      o !== void 0 && this._$Eh.set(o, i);
    }
    this.elementStyles = this.finalizeStyles(this.styles);
  }
  static finalizeStyles(t) {
    const i = [];
    if (Array.isArray(t)) {
      const n = new Set(t.flat(1 / 0).reverse());
      for (const o of n) i.unshift(Gt(o));
    } else t !== void 0 && i.push(Gt(t));
    return i;
  }
  static _$Eu(t, i) {
    const n = i.attribute;
    return n === !1 ? void 0 : typeof n == "string" ? n : typeof t == "string" ? t.toLowerCase() : void 0;
  }
  constructor() {
    super(), this._$Ep = void 0, this.isUpdatePending = !1, this.hasUpdated = !1, this._$Em = null, this._$Ev();
  }
  _$Ev() {
    this._$ES = new Promise((t) => this.enableUpdating = t), this._$AL = /* @__PURE__ */ new Map(), this._$E_(), this.requestUpdate(), this.constructor.l?.forEach((t) => t(this));
  }
  addController(t) {
    (this._$EO ??= /* @__PURE__ */ new Set()).add(t), this.renderRoot !== void 0 && this.isConnected && t.hostConnected?.();
  }
  removeController(t) {
    this._$EO?.delete(t);
  }
  _$E_() {
    const t = /* @__PURE__ */ new Map(), i = this.constructor.elementProperties;
    for (const n of i.keys()) this.hasOwnProperty(n) && (t.set(n, this[n]), delete this[n]);
    t.size > 0 && (this._$Ep = t);
  }
  createRenderRoot() {
    const t = this.shadowRoot ?? this.attachShadow(this.constructor.shadowRootOptions);
    return Re(t, this.constructor.elementStyles), t;
  }
  connectedCallback() {
    this.renderRoot ??= this.createRenderRoot(), this.enableUpdating(!0), this._$EO?.forEach((t) => t.hostConnected?.());
  }
  enableUpdating(t) {
  }
  disconnectedCallback() {
    this._$EO?.forEach((t) => t.hostDisconnected?.());
  }
  attributeChangedCallback(t, i, n) {
    this._$AK(t, n);
  }
  _$ET(t, i) {
    const n = this.constructor.elementProperties.get(t), o = this.constructor._$Eu(t, n);
    if (o !== void 0 && n.reflect === !0) {
      const r = (n.converter?.toAttribute !== void 0 ? n.converter : $t).toAttribute(i, n.type);
      this._$Em = t, r == null ? this.removeAttribute(o) : this.setAttribute(o, r), this._$Em = null;
    }
  }
  _$AK(t, i) {
    const n = this.constructor, o = n._$Eh.get(t);
    if (o !== void 0 && this._$Em !== o) {
      const r = n.getPropertyOptions(o), s = typeof r.converter == "function" ? { fromAttribute: r.converter } : r.converter?.fromAttribute !== void 0 ? r.converter : $t;
      this._$Em = o, this[o] = s.fromAttribute(i, r.type) ?? this._$Ej?.get(o) ?? null, this._$Em = null;
    }
  }
  requestUpdate(t, i, n) {
    if (t !== void 0) {
      const o = this.constructor, r = this[t];
      if (n ??= o.getPropertyOptions(t), !((n.hasChanged ?? It)(r, i) || n.useDefault && n.reflect && r === this._$Ej?.get(t) && !this.hasAttribute(o._$Eu(t, n)))) return;
      this.C(t, i, n);
    }
    this.isUpdatePending === !1 && (this._$ES = this._$EP());
  }
  C(t, i, { useDefault: n, reflect: o, wrapped: r }, s) {
    n && !(this._$Ej ??= /* @__PURE__ */ new Map()).has(t) && (this._$Ej.set(t, s ?? i ?? this[t]), r !== !0 || s !== void 0) || (this._$AL.has(t) || (this.hasUpdated || n || (i = void 0), this._$AL.set(t, i)), o === !0 && this._$Em !== t && (this._$Eq ??= /* @__PURE__ */ new Set()).add(t));
  }
  async _$EP() {
    this.isUpdatePending = !0;
    try {
      await this._$ES;
    } catch (i) {
      Promise.reject(i);
    }
    const t = this.scheduleUpdate();
    return t != null && await t, !this.isUpdatePending;
  }
  scheduleUpdate() {
    return this.performUpdate();
  }
  performUpdate() {
    if (!this.isUpdatePending) return;
    if (!this.hasUpdated) {
      if (this.renderRoot ??= this.createRenderRoot(), this._$Ep) {
        for (const [o, r] of this._$Ep) this[o] = r;
        this._$Ep = void 0;
      }
      const n = this.constructor.elementProperties;
      if (n.size > 0) for (const [o, r] of n) {
        const { wrapped: s } = r, l = this[o];
        s !== !0 || this._$AL.has(o) || l === void 0 || this.C(o, void 0, r, l);
      }
    }
    let t = !1;
    const i = this._$AL;
    try {
      t = this.shouldUpdate(i), t ? (this.willUpdate(i), this._$EO?.forEach((n) => n.hostUpdate?.()), this.update(i)) : this._$EM();
    } catch (n) {
      throw t = !1, this._$EM(), n;
    }
    t && this._$AE(i);
  }
  willUpdate(t) {
  }
  _$AE(t) {
    this._$EO?.forEach((i) => i.hostUpdated?.()), this.hasUpdated || (this.hasUpdated = !0, this.firstUpdated(t)), this.updated(t);
  }
  _$EM() {
    this._$AL = /* @__PURE__ */ new Map(), this.isUpdatePending = !1;
  }
  get updateComplete() {
    return this.getUpdateComplete();
  }
  getUpdateComplete() {
    return this._$ES;
  }
  shouldUpdate(t) {
    return !0;
  }
  update(t) {
    this._$Eq &&= this._$Eq.forEach((i) => this._$ET(i, this[i])), this._$EM();
  }
  updated(t) {
  }
  firstUpdated(t) {
  }
};
J.elementStyles = [], J.shadowRootOptions = { mode: "open" }, J[ct("elementProperties")] = /* @__PURE__ */ new Map(), J[ct("finalized")] = /* @__PURE__ */ new Map(), ze?.({ ReactiveElement: J }), (Ct.reactiveElementVersions ??= []).push("2.1.0");
/**
 * @license
 * Copyright 2017 Google LLC
 * SPDX-License-Identifier: BSD-3-Clause
 */
const zt = globalThis, At = zt.trustedTypes, Qt = At ? At.createPolicy("lit-html", { createHTML: (e) => e }) : void 0, me = "$lit$", N = `lit$${Math.random().toFixed(9).slice(2)}$`, we = "?" + N, Be = `<${we}>`, X = document, dt = () => X.createComment(""), ft = (e) => e === null || typeof e != "object" && typeof e != "function", Bt = Array.isArray, je = (e) => Bt(e) || typeof e?.[Symbol.iterator] == "function", Lt = `[ 	
\f\r]`, lt = /<(?:(!--|\/[^a-zA-Z])|(\/?[a-zA-Z][^>\s]*)|(\/?$))/g, te = /-->/g, ee = />/g, W = RegExp(`>|${Lt}(?:([^\\s"'>=/]+)(${Lt}*=${Lt}*(?:[^ 	
\f\r"'\`<>=]|("|')|))|$)`, "g"), ie = /'/g, ne = /"/g, ge = /^(?:script|style|textarea|title)$/i, Fe = (e) => (t, ...i) => ({ _$litType$: e, strings: t, values: i }), We = Fe(1), Q = Symbol.for("lit-noChange"), x = Symbol.for("lit-nothing"), oe = /* @__PURE__ */ new WeakMap(), V = X.createTreeWalker(X, 129);
function ye(e, t) {
  if (!Bt(e) || !e.hasOwnProperty("raw")) throw Error("invalid template strings array");
  return Qt !== void 0 ? Qt.createHTML(t) : t;
}
const Ve = (e, t) => {
  const i = e.length - 1, n = [];
  let o, r = t === 2 ? "<svg>" : t === 3 ? "<math>" : "", s = lt;
  for (let l = 0; l < i; l++) {
    const a = e[l];
    let c, f, h = -1, p = 0;
    for (; p < a.length && (s.lastIndex = p, f = s.exec(a), f !== null); ) p = s.lastIndex, s === lt ? f[1] === "!--" ? s = te : f[1] !== void 0 ? s = ee : f[2] !== void 0 ? (ge.test(f[2]) && (o = RegExp("</" + f[2], "g")), s = W) : f[3] !== void 0 && (s = W) : s === W ? f[0] === ">" ? (s = o ?? lt, h = -1) : f[1] === void 0 ? h = -2 : (h = s.lastIndex - f[2].length, c = f[1], s = f[3] === void 0 ? W : f[3] === '"' ? ne : ie) : s === ne || s === ie ? s = W : s === te || s === ee ? s = lt : (s = W, o = void 0);
    const d = s === W && e[l + 1].startsWith("/>") ? " " : "";
    r += s === lt ? a + Be : h >= 0 ? (n.push(c), a.slice(0, h) + me + a.slice(h) + N + d) : a + N + (h === -2 ? l : d);
  }
  return [ye(e, r + (e[i] || "<?>") + (t === 2 ? "</svg>" : t === 3 ? "</math>" : "")), n];
};
class pt {
  constructor({ strings: t, _$litType$: i }, n) {
    let o;
    this.parts = [];
    let r = 0, s = 0;
    const l = t.length - 1, a = this.parts, [c, f] = Ve(t, i);
    if (this.el = pt.createElement(c, n), V.currentNode = this.el.content, i === 2 || i === 3) {
      const h = this.el.content.firstChild;
      h.replaceWith(...h.childNodes);
    }
    for (; (o = V.nextNode()) !== null && a.length < l; ) {
      if (o.nodeType === 1) {
        if (o.hasAttributes()) for (const h of o.getAttributeNames()) if (h.endsWith(me)) {
          const p = f[s++], d = o.getAttribute(h).split(N), u = /([.?@])?(.*)/.exec(p);
          a.push({ type: 1, index: r, name: u[2], strings: d, ctor: u[1] === "." ? Xe : u[1] === "?" ? Ye : u[1] === "@" ? Ke : kt }), o.removeAttribute(h);
        } else h.startsWith(N) && (a.push({ type: 6, index: r }), o.removeAttribute(h));
        if (ge.test(o.tagName)) {
          const h = o.textContent.split(N), p = h.length - 1;
          if (p > 0) {
            o.textContent = At ? At.emptyScript : "";
            for (let d = 0; d < p; d++) o.append(h[d], dt()), V.nextNode(), a.push({ type: 2, index: ++r });
            o.append(h[p], dt());
          }
        }
      } else if (o.nodeType === 8) if (o.data === we) a.push({ type: 2, index: r });
      else {
        let h = -1;
        for (; (h = o.data.indexOf(N, h + 1)) !== -1; ) a.push({ type: 7, index: r }), h += N.length - 1;
      }
      r++;
    }
  }
  static createElement(t, i) {
    const n = X.createElement("template");
    return n.innerHTML = t, n;
  }
}
function tt(e, t, i = e, n) {
  if (t === Q) return t;
  let o = n !== void 0 ? i._$Co?.[n] : i._$Cl;
  const r = ft(t) ? void 0 : t._$litDirective$;
  return o?.constructor !== r && (o?._$AO?.(!1), r === void 0 ? o = void 0 : (o = new r(e), o._$AT(e, i, n)), n !== void 0 ? (i._$Co ??= [])[n] = o : i._$Cl = o), o !== void 0 && (t = tt(e, o._$AS(e, t.values), o, n)), t;
}
class qe {
  constructor(t, i) {
    this._$AV = [], this._$AN = void 0, this._$AD = t, this._$AM = i;
  }
  get parentNode() {
    return this._$AM.parentNode;
  }
  get _$AU() {
    return this._$AM._$AU;
  }
  u(t) {
    const { el: { content: i }, parts: n } = this._$AD, o = (t?.creationScope ?? X).importNode(i, !0);
    V.currentNode = o;
    let r = V.nextNode(), s = 0, l = 0, a = n[0];
    for (; a !== void 0; ) {
      if (s === a.index) {
        let c;
        a.type === 2 ? c = new mt(r, r.nextSibling, this, t) : a.type === 1 ? c = new a.ctor(r, a.name, a.strings, this, t) : a.type === 6 && (c = new Ge(r, this, t)), this._$AV.push(c), a = n[++l];
      }
      s !== a?.index && (r = V.nextNode(), s++);
    }
    return V.currentNode = X, o;
  }
  p(t) {
    let i = 0;
    for (const n of this._$AV) n !== void 0 && (n.strings !== void 0 ? (n._$AI(t, n, i), i += n.strings.length - 2) : n._$AI(t[i])), i++;
  }
}
class mt {
  get _$AU() {
    return this._$AM?._$AU ?? this._$Cv;
  }
  constructor(t, i, n, o) {
    this.type = 2, this._$AH = x, this._$AN = void 0, this._$AA = t, this._$AB = i, this._$AM = n, this.options = o, this._$Cv = o?.isConnected ?? !0;
  }
  get parentNode() {
    let t = this._$AA.parentNode;
    const i = this._$AM;
    return i !== void 0 && t?.nodeType === 11 && (t = i.parentNode), t;
  }
  get startNode() {
    return this._$AA;
  }
  get endNode() {
    return this._$AB;
  }
  _$AI(t, i = this) {
    t = tt(this, t, i), ft(t) ? t === x || t == null || t === "" ? (this._$AH !== x && this._$AR(), this._$AH = x) : t !== this._$AH && t !== Q && this._(t) : t._$litType$ !== void 0 ? this.$(t) : t.nodeType !== void 0 ? this.T(t) : je(t) ? this.k(t) : this._(t);
  }
  O(t) {
    return this._$AA.parentNode.insertBefore(t, this._$AB);
  }
  T(t) {
    this._$AH !== t && (this._$AR(), this._$AH = this.O(t));
  }
  _(t) {
    this._$AH !== x && ft(this._$AH) ? this._$AA.nextSibling.data = t : this.T(X.createTextNode(t)), this._$AH = t;
  }
  $(t) {
    const { values: i, _$litType$: n } = t, o = typeof n == "number" ? this._$AC(t) : (n.el === void 0 && (n.el = pt.createElement(ye(n.h, n.h[0]), this.options)), n);
    if (this._$AH?._$AD === o) this._$AH.p(i);
    else {
      const r = new qe(o, this), s = r.u(this.options);
      r.p(i), this.T(s), this._$AH = r;
    }
  }
  _$AC(t) {
    let i = oe.get(t.strings);
    return i === void 0 && oe.set(t.strings, i = new pt(t)), i;
  }
  k(t) {
    Bt(this._$AH) || (this._$AH = [], this._$AR());
    const i = this._$AH;
    let n, o = 0;
    for (const r of t) o === i.length ? i.push(n = new mt(this.O(dt()), this.O(dt()), this, this.options)) : n = i[o], n._$AI(r), o++;
    o < i.length && (this._$AR(n && n._$AB.nextSibling, o), i.length = o);
  }
  _$AR(t = this._$AA.nextSibling, i) {
    for (this._$AP?.(!1, !0, i); t && t !== this._$AB; ) {
      const n = t.nextSibling;
      t.remove(), t = n;
    }
  }
  setConnected(t) {
    this._$AM === void 0 && (this._$Cv = t, this._$AP?.(t));
  }
}
class kt {
  get tagName() {
    return this.element.tagName;
  }
  get _$AU() {
    return this._$AM._$AU;
  }
  constructor(t, i, n, o, r) {
    this.type = 1, this._$AH = x, this._$AN = void 0, this.element = t, this.name = i, this._$AM = o, this.options = r, n.length > 2 || n[0] !== "" || n[1] !== "" ? (this._$AH = Array(n.length - 1).fill(new String()), this.strings = n) : this._$AH = x;
  }
  _$AI(t, i = this, n, o) {
    const r = this.strings;
    let s = !1;
    if (r === void 0) t = tt(this, t, i, 0), s = !ft(t) || t !== this._$AH && t !== Q, s && (this._$AH = t);
    else {
      const l = t;
      let a, c;
      for (t = r[0], a = 0; a < r.length - 1; a++) c = tt(this, l[n + a], i, a), c === Q && (c = this._$AH[a]), s ||= !ft(c) || c !== this._$AH[a], c === x ? t = x : t !== x && (t += (c ?? "") + r[a + 1]), this._$AH[a] = c;
    }
    s && !o && this.j(t);
  }
  j(t) {
    t === x ? this.element.removeAttribute(this.name) : this.element.setAttribute(this.name, t ?? "");
  }
}
class Xe extends kt {
  constructor() {
    super(...arguments), this.type = 3;
  }
  j(t) {
    this.element[this.name] = t === x ? void 0 : t;
  }
}
class Ye extends kt {
  constructor() {
    super(...arguments), this.type = 4;
  }
  j(t) {
    this.element.toggleAttribute(this.name, !!t && t !== x);
  }
}
class Ke extends kt {
  constructor(t, i, n, o, r) {
    super(t, i, n, o, r), this.type = 5;
  }
  _$AI(t, i = this) {
    if ((t = tt(this, t, i, 0) ?? x) === Q) return;
    const n = this._$AH, o = t === x && n !== x || t.capture !== n.capture || t.once !== n.once || t.passive !== n.passive, r = t !== x && (n === x || o);
    o && this.element.removeEventListener(this.name, this, n), r && this.element.addEventListener(this.name, this, t), this._$AH = t;
  }
  handleEvent(t) {
    typeof this._$AH == "function" ? this._$AH.call(this.options?.host ?? this.element, t) : this._$AH.handleEvent(t);
  }
}
class Ge {
  constructor(t, i, n) {
    this.element = t, this.type = 6, this._$AN = void 0, this._$AM = i, this.options = n;
  }
  get _$AU() {
    return this._$AM._$AU;
  }
  _$AI(t) {
    tt(this, t);
  }
}
const Je = zt.litHtmlPolyfillSupport;
Je?.(pt, mt), (zt.litHtmlVersions ??= []).push("3.3.0");
const Ze = (e, t, i) => {
  const n = i?.renderBefore ?? t;
  let o = n._$litPart$;
  if (o === void 0) {
    const r = i?.renderBefore ?? null;
    n._$litPart$ = o = new mt(t.insertBefore(dt(), r), r, void 0, i ?? {});
  }
  return o._$AI(e), o;
};
/**
 * @license
 * Copyright 2017 Google LLC
 * SPDX-License-Identifier: BSD-3-Clause
 */
const jt = globalThis;
class ht extends J {
  constructor() {
    super(...arguments), this.renderOptions = { host: this }, this._$Do = void 0;
  }
  createRenderRoot() {
    const t = super.createRenderRoot();
    return this.renderOptions.renderBefore ??= t.firstChild, t;
  }
  update(t) {
    const i = this.render();
    this.hasUpdated || (this.renderOptions.isConnected = this.isConnected), super.update(t), this._$Do = Ze(i, this.renderRoot, this.renderOptions);
  }
  connectedCallback() {
    super.connectedCallback(), this._$Do?.setConnected(!0);
  }
  disconnectedCallback() {
    super.disconnectedCallback(), this._$Do?.setConnected(!1);
  }
  render() {
    return Q;
  }
}
ht._$litElement$ = !0, ht.finalized = !0, jt.litElementHydrateSupport?.({ LitElement: ht });
const Qe = jt.litElementPolyfillSupport;
Qe?.({ LitElement: ht });
(jt.litElementVersions ??= []).push("4.2.0");
/**
 * @license
 * Copyright 2020 Google LLC
 * SPDX-License-Identifier: BSD-3-Clause
 */
const ti = Symbol.for(""), ei = (e) => {
  if (e?.r === ti) return e?._$litStatic$;
}, re = /* @__PURE__ */ new Map(), ii = (e) => (t, ...i) => {
  const n = i.length;
  let o, r;
  const s = [], l = [];
  let a, c = 0, f = !1;
  for (; c < n; ) {
    for (a = t[c]; c < n && (r = i[c], (o = ei(r)) !== void 0); ) a += o + t[++c], f = !0;
    c !== n && l.push(r), s.push(a), c++;
  }
  if (c === n && s.push(t[n]), f) {
    const h = s.join("$$lit$$");
    (t = re.get(h)) === void 0 && (s.raw = s, re.set(h, t = s)), i = l;
  }
  return e(t, ...i);
}, vt = ii(We);
/**
 * @license
 * Copyright 2017 Google LLC
 * SPDX-License-Identifier: BSD-3-Clause
 */
const ni = { attribute: !0, type: String, converter: $t, reflect: !1, hasChanged: It }, oi = (e = ni, t, i) => {
  const { kind: n, metadata: o } = i;
  let r = globalThis.litPropertyMetadata.get(o);
  if (r === void 0 && globalThis.litPropertyMetadata.set(o, r = /* @__PURE__ */ new Map()), n === "setter" && ((e = Object.create(e)).wrapped = !0), r.set(i.name, e), n === "accessor") {
    const { name: s } = i;
    return { set(l) {
      const a = t.get.call(this);
      t.set.call(this, l), this.requestUpdate(s, a, e);
    }, init(l) {
      return l !== void 0 && this.C(s, void 0, e, l), l;
    } };
  }
  if (n === "setter") {
    const { name: s } = i;
    return function(l) {
      const a = this[s];
      t.call(this, l), this.requestUpdate(s, a, e);
    };
  }
  throw Error("Unsupported decorator location: " + n);
};
function nt(e) {
  return (t, i) => typeof i == "object" ? oi(e, t, i) : ((n, o, r) => {
    const s = o.hasOwnProperty(r);
    return o.constructor.createProperty(r, n), s ? Object.getOwnPropertyDescriptor(o, r) : void 0;
  })(e, t, i);
}
/**
 * @license
 * Copyright 2017 Google LLC
 * SPDX-License-Identifier: BSD-3-Clause
 */
const ri = (e, t, i) => (i.configurable = !0, i.enumerable = !0, Reflect.decorate && typeof t != "object" && Object.defineProperty(e, t, i), i);
/**
 * @license
 * Copyright 2017 Google LLC
 * SPDX-License-Identifier: BSD-3-Clause
 */
function si(e, t) {
  return (i, n, o) => {
    const r = (s) => s.renderRoot?.querySelector(e) ?? null;
    return ri(i, n, { get() {
      return r(this);
    } });
  };
}
const ai = '/*! tailwindcss v4.1.12 | MIT License | https://tailwindcss.com */@layer properties{@supports (((-webkit-hyphens:none)) and (not (margin-trim:inline))) or ((-moz-orient:inline) and (not (color:rgb(from red r g b)))){*,:before,:after,::backdrop{--tw-rotate-x:initial;--tw-rotate-y:initial;--tw-rotate-z:initial;--tw-skew-x:initial;--tw-skew-y:initial;--tw-border-style:solid;--tw-shadow:0 0 #0000;--tw-shadow-color:initial;--tw-shadow-alpha:100%;--tw-inset-shadow:0 0 #0000;--tw-inset-shadow-color:initial;--tw-inset-shadow-alpha:100%;--tw-ring-color:initial;--tw-ring-shadow:0 0 #0000;--tw-inset-ring-color:initial;--tw-inset-ring-shadow:0 0 #0000;--tw-ring-inset:initial;--tw-ring-offset-width:0px;--tw-ring-offset-color:#fff;--tw-ring-offset-shadow:0 0 #0000;--tw-blur:initial;--tw-brightness:initial;--tw-contrast:initial;--tw-grayscale:initial;--tw-hue-rotate:initial;--tw-invert:initial;--tw-opacity:initial;--tw-saturate:initial;--tw-sepia:initial;--tw-drop-shadow:initial;--tw-drop-shadow-color:initial;--tw-drop-shadow-alpha:100%;--tw-drop-shadow-size:initial}}}:root,:host{--default-transition-duration:.15s;--default-transition-timing-function:cubic-bezier(.4,0,.2,1)}.visible{visibility:visible}.absolute{position:absolute}.fixed{position:fixed}.relative{position:relative}.static{position:static}.-top-8{top:calc(var(--bdk-space-8,5.33333rem)*-1)}.isolate{isolation:isolate}.container{width:100%}@media (min-width:319px){.container{max-width:319px}}@media (min-width:320px){.container{max-width:320px}}@media (min-width:359px){.container{max-width:359px}}@media (min-width:360px){.container{max-width:360px}}@media (min-width:374px){.container{max-width:374px}}@media (min-width:375px){.container{max-width:375px}}@media (min-width:559px){.container{max-width:559px}}@media (min-width:560px){.container{max-width:560px}}@media (min-width:759px){.container{max-width:759px}}@media (min-width:760px){.container{max-width:760px}}@media (min-width:959px){.container{max-width:959px}}@media (min-width:960px){.container{max-width:960px}}@media (min-width:1159px){.container{max-width:1159px}}@media (min-width:1160px){.container{max-width:1160px}}@media (min-width:1359px){.container{max-width:1359px}}@media (min-width:1360px){.container{max-width:1360px}}@media (min-width:1659px){.container{max-width:1659px}}@media (min-width:1660px){.container{max-width:1660px}}@media (min-width:1919px){.container{max-width:1919px}}@media (min-width:1920px){.container{max-width:1920px}}@media (min-width:40rem){.container{max-width:40rem}}@media (min-width:48rem){.container{max-width:48rem}}@media (min-width:64rem){.container{max-width:64rem}}@media (min-width:80rem){.container{max-width:80rem}}@media (min-width:96rem){.container{max-width:96rem}}.m-4{margin:var(--bdk-space-4,2.66667rem)}.mx-auto{margin-inline:auto}.mt-4{margin-top:var(--bdk-space-4,2.66667rem)}.mt-6{margin-top:var(--bdk-space-6,4rem)}.mt-12{margin-top:var(--bdk-space-12,8rem)}.mb-8{margin-bottom:var(--bdk-space-8,5.33333rem)}.block{display:block}.contents{display:contents}.flex{display:flex}.grid{display:grid}.hidden{display:none}.inline{display:inline}.table{display:table}.h-8{height:var(--bdk-space-8,5.33333rem)}.h-10{height:var(--bdk-space-10,6.66667rem)}.h-32{height:var(--bdk-space-32,21.3333rem)}.h-full{height:100%}.w-8{width:var(--bdk-space-8,5.33333rem)}.w-10{width:var(--bdk-space-10,6.66667rem)}.w-32{width:var(--bdk-space-32,21.3333rem)}.transform{transform:var(--tw-rotate-x,)var(--tw-rotate-y,)var(--tw-rotate-z,)var(--tw-skew-x,)var(--tw-skew-y,)}.resize{resize:both}.grid-cols-3{grid-template-columns:repeat(3,minmax(0,1fr))}.flex-col{flex-direction:column}.flex-wrap{flex-wrap:wrap}.place-items-center{place-items:center}.items-center{align-items:center}.justify-center{justify-content:center}.gap-4{gap:var(--bdk-space-4,2.66667rem)}.gap-8{gap:var(--bdk-space-8,5.33333rem)}.border{border-style:var(--tw-border-style);border-width:1px}.border-2{border-style:var(--tw-border-style);border-width:2px}.border-dashed{--tw-border-style:dashed;border-style:dashed}.bg-pebble{background-color:var(--bdk-pebble,#e0dfde)}.pt-16{padding-top:var(--bdk-space-16,10.6667rem)}.pb-24{padding-bottom:var(--bdk-space-24,16rem)}.text-center{text-align:center}.text-inherit{color:inherit}.lowercase{text-transform:lowercase}.shadow{--tw-shadow:0 1px 3px 0 var(--tw-shadow-color,#0000001a),0 1px 2px -1px var(--tw-shadow-color,#0000001a);box-shadow:var(--tw-inset-shadow),var(--tw-inset-ring-shadow),var(--tw-ring-offset-shadow),var(--tw-ring-shadow),var(--tw-shadow)}.blur{--tw-blur:blur(8px);filter:var(--tw-blur,)var(--tw-brightness,)var(--tw-contrast,)var(--tw-grayscale,)var(--tw-hue-rotate,)var(--tw-invert,)var(--tw-saturate,)var(--tw-sepia,)var(--tw-drop-shadow,)}.filter{filter:var(--tw-blur,)var(--tw-brightness,)var(--tw-contrast,)var(--tw-grayscale,)var(--tw-hue-rotate,)var(--tw-invert,)var(--tw-saturate,)var(--tw-sepia,)var(--tw-drop-shadow,)}.transition{transition-property:color,background-color,border-color,outline-color,text-decoration-color,fill,stroke,--tw-gradient-from,--tw-gradient-via,--tw-gradient-to,opacity,box-shadow,transform,translate,scale,rotate,filter,-webkit-backdrop-filter,backdrop-filter,display,visibility,content-visibility,overlay,pointer-events;transition-timing-function:var(--tw-ease,var(--default-transition-timing-function));transition-duration:var(--tw-duration,var(--default-transition-duration))}.select-none{-webkit-user-select:none;user-select:none}@media (min-width:48rem){.md\\:flex-row{flex-direction:row}}:host(:where([popover])){margin:unset;inset:unset;overflow:unset;padding:unset;border:0}:host(:not(:popover-open)){display:none}:host{--sqrt2:1.4142;--bdk-tooltip-arrow-size:var(--bdk-space-6,24px);--bdk-tooltip-arrow-side:calc(var(--bdk-tooltip-arrow-size)/var(--sqrt2));--bdk-tooltip-max-width:min(100dvw,324px);--bdk-tooltip-offset:var(--bdk-space-4,16px);max-width:var(--bdk-tooltip-max-width);text-align:start;background-color:var(--bdk-coal-100,#000);width:max-content;transition:opacity var(--bdk-transition-duration-default,.2s)ease,visibility var(--bdk-transition-duration-default,.2s)ease;color:var(--bdk-snow-100,#fff);border-radius:8px;display:inline-block;position:relative;overflow:visible}:host:before{content:"";top:calc(var(--bdk-tooltip-offset)*-1);left:calc(var(--bdk-tooltip-offset)*-1);border:var(--bdk-tooltip-offset),solid,transparent;width:100%;height:100%;position:absolute}.tooltip--arrow{width:var(--bdk-tooltip-arrow-side);aspect-ratio:1;background-color:var(--bdk-coal-100);border-radius:2px;position:absolute;transform:rotate(45deg)}.tooltip--content{gap:var(--bdk-space-2,8px);padding:var(--bdk-space-4,16px);box-shadow:var(--bdk-shadow-elevation--1);word-wrap:break-word;display:grid}.tooltip--content:has(.tooltip--icon){grid-template-columns:auto 1fr}.tooltip--content:has(.tooltip--icon) .tooltip--icon{grid-column:1}.tooltip--content:has(.tooltip--icon) .tooltip--header,.tooltip--content:has(.tooltip--icon) .tooltip--text{grid-column:2}.tooltip--content:not(:has(.tooltip--icon)){grid-template-columns:1fr}.tooltip--content:not(:has(.tooltip--icon)) .tooltip--header,.tooltip--content:not(:has(.tooltip--icon)) .tooltip--text{grid-column:1}.tooltip--header{letter-spacing:.05rem;text-transform:uppercase;grid-row:1;align-self:center;font-family:Eduard Breuninger;font-size:2.66667rem;font-weight:500;line-height:4rem;display:flex}.tooltip--text{letter-spacing:.05rem;text-transform:inherit;text-wrap:wrap;grid-area:1/2;align-self:center;font-family:Eduard Breuninger;font-size:2.33333rem;font-weight:500;line-height:3.66667rem}.tooltip--header+.tooltip--text{grid-row:2;align-self:self-start}.tooltip--icon{align-self:start;align-items:center;display:inline-flex}@property --tw-rotate-x{syntax:"*";inherits:false}@property --tw-rotate-y{syntax:"*";inherits:false}@property --tw-rotate-z{syntax:"*";inherits:false}@property --tw-skew-x{syntax:"*";inherits:false}@property --tw-skew-y{syntax:"*";inherits:false}@property --tw-border-style{syntax:"*";inherits:false;initial-value:solid}@property --tw-shadow{syntax:"*";inherits:false;initial-value:0 0 #0000}@property --tw-shadow-color{syntax:"*";inherits:false}@property --tw-shadow-alpha{syntax:"<percentage>";inherits:false;initial-value:100%}@property --tw-inset-shadow{syntax:"*";inherits:false;initial-value:0 0 #0000}@property --tw-inset-shadow-color{syntax:"*";inherits:false}@property --tw-inset-shadow-alpha{syntax:"<percentage>";inherits:false;initial-value:100%}@property --tw-ring-color{syntax:"*";inherits:false}@property --tw-ring-shadow{syntax:"*";inherits:false;initial-value:0 0 #0000}@property --tw-inset-ring-color{syntax:"*";inherits:false}@property --tw-inset-ring-shadow{syntax:"*";inherits:false;initial-value:0 0 #0000}@property --tw-ring-inset{syntax:"*";inherits:false}@property --tw-ring-offset-width{syntax:"<length>";inherits:false;initial-value:0}@property --tw-ring-offset-color{syntax:"*";inherits:false;initial-value:#fff}@property --tw-ring-offset-shadow{syntax:"*";inherits:false;initial-value:0 0 #0000}@property --tw-blur{syntax:"*";inherits:false}@property --tw-brightness{syntax:"*";inherits:false}@property --tw-contrast{syntax:"*";inherits:false}@property --tw-grayscale{syntax:"*";inherits:false}@property --tw-hue-rotate{syntax:"*";inherits:false}@property --tw-invert{syntax:"*";inherits:false}@property --tw-opacity{syntax:"*";inherits:false}@property --tw-saturate{syntax:"*";inherits:false}@property --tw-sepia{syntax:"*";inherits:false}@property --tw-drop-shadow{syntax:"*";inherits:false}@property --tw-drop-shadow-color{syntax:"*";inherits:false}@property --tw-drop-shadow-alpha{syntax:"<percentage>";inherits:false;initial-value:100%}@property --tw-drop-shadow-size{syntax:"*";inherits:false}', li = () => typeof crypto < "u" && crypto.randomUUID ? crypto.randomUUID() : "", ci = () => {
  let e = 0;
  const t = Date.now(), i = Math.floor(Math.random() * 1e3), n = `${t}-${i}`;
  return function* (o) {
    for (; ; )
      yield `${o}-${n}-${++e}`;
  };
}, hi = ci(), di = (e) => {
  const t = li();
  return t ? `${e}-${t}` : hi(e).next().value;
}, et = Math.min, q = Math.max, _t = Math.round, bt = Math.floor, P = (e) => ({
  x: e,
  y: e
}), fi = {
  left: "right",
  right: "left",
  bottom: "top",
  top: "bottom"
}, pi = {
  start: "end",
  end: "start"
};
function Mt(e, t, i) {
  return q(e, et(t, i));
}
function wt(e, t) {
  return typeof e == "function" ? e(t) : e;
}
function Y(e) {
  return e.split("-")[0];
}
function gt(e) {
  return e.split("-")[1];
}
function ve(e) {
  return e === "x" ? "y" : "x";
}
function Ft(e) {
  return e === "y" ? "height" : "width";
}
const ui = /* @__PURE__ */ new Set(["top", "bottom"]);
function I(e) {
  return ui.has(Y(e)) ? "y" : "x";
}
function Wt(e) {
  return ve(I(e));
}
function mi(e, t, i) {
  i === void 0 && (i = !1);
  const n = gt(e), o = Wt(e), r = Ft(o);
  let s = o === "x" ? n === (i ? "end" : "start") ? "right" : "left" : n === "start" ? "bottom" : "top";
  return t.reference[r] > t.floating[r] && (s = Et(s)), [s, Et(s)];
}
function wi(e) {
  const t = Et(e);
  return [Ut(e), t, Ut(t)];
}
function Ut(e) {
  return e.replace(/start|end/g, (t) => pi[t]);
}
const se = ["left", "right"], ae = ["right", "left"], gi = ["top", "bottom"], yi = ["bottom", "top"];
function vi(e, t, i) {
  switch (e) {
    case "top":
    case "bottom":
      return i ? t ? ae : se : t ? se : ae;
    case "left":
    case "right":
      return t ? gi : yi;
    default:
      return [];
  }
}
function bi(e, t, i, n) {
  const o = gt(e);
  let r = vi(Y(e), i === "start", n);
  return o && (r = r.map((s) => s + "-" + o), t && (r = r.concat(r.map(Ut)))), r;
}
function Et(e) {
  return e.replace(/left|right|bottom|top/g, (t) => fi[t]);
}
function xi(e) {
  return {
    top: 0,
    right: 0,
    bottom: 0,
    left: 0,
    ...e
  };
}
function be(e) {
  return typeof e != "number" ? xi(e) : {
    top: e,
    right: e,
    bottom: e,
    left: e
  };
}
function St(e) {
  const {
    x: t,
    y: i,
    width: n,
    height: o
  } = e;
  return {
    width: n,
    height: o,
    top: i,
    left: t,
    right: t + n,
    bottom: i + o,
    x: t,
    y: i
  };
}
function le(e, t, i) {
  let {
    reference: n,
    floating: o
  } = e;
  const r = I(t), s = Wt(t), l = Ft(s), a = Y(t), c = r === "y", f = n.x + n.width / 2 - o.width / 2, h = n.y + n.height / 2 - o.height / 2, p = n[l] / 2 - o[l] / 2;
  let d;
  switch (a) {
    case "top":
      d = {
        x: f,
        y: n.y - o.height
      };
      break;
    case "bottom":
      d = {
        x: f,
        y: n.y + n.height
      };
      break;
    case "right":
      d = {
        x: n.x + n.width,
        y: h
      };
      break;
    case "left":
      d = {
        x: n.x - o.width,
        y: h
      };
      break;
    default:
      d = {
        x: n.x,
        y: n.y
      };
  }
  switch (gt(t)) {
    case "start":
      d[s] -= p * (i && c ? -1 : 1);
      break;
    case "end":
      d[s] += p * (i && c ? -1 : 1);
      break;
  }
  return d;
}
const $i = async (e, t, i) => {
  const {
    placement: n = "bottom",
    strategy: o = "absolute",
    middleware: r = [],
    platform: s
  } = i, l = r.filter(Boolean), a = await (s.isRTL == null ? void 0 : s.isRTL(t));
  let c = await s.getElementRects({
    reference: e,
    floating: t,
    strategy: o
  }), {
    x: f,
    y: h
  } = le(c, n, a), p = n, d = {}, u = 0;
  for (let m = 0; m < l.length; m++) {
    const {
      name: g,
      fn: w
    } = l[m], {
      x: y,
      y: v,
      data: $,
      reset: b
    } = await w({
      x: f,
      y: h,
      initialPlacement: n,
      placement: p,
      strategy: o,
      middlewareData: d,
      rects: c,
      platform: s,
      elements: {
        reference: e,
        floating: t
      }
    });
    f = y ?? f, h = v ?? h, d = {
      ...d,
      [g]: {
        ...d[g],
        ...$
      }
    }, b && u <= 50 && (u++, typeof b == "object" && (b.placement && (p = b.placement), b.rects && (c = b.rects === !0 ? await s.getElementRects({
      reference: e,
      floating: t,
      strategy: o
    }) : b.rects), {
      x: f,
      y: h
    } = le(c, p, a)), m = -1);
  }
  return {
    x: f,
    y: h,
    placement: p,
    strategy: o,
    middlewareData: d
  };
};
async function xe(e, t) {
  var i;
  t === void 0 && (t = {});
  const {
    x: n,
    y: o,
    platform: r,
    rects: s,
    elements: l,
    strategy: a
  } = e, {
    boundary: c = "clippingAncestors",
    rootBoundary: f = "viewport",
    elementContext: h = "floating",
    altBoundary: p = !1,
    padding: d = 0
  } = wt(t, e), u = be(d), g = l[p ? h === "floating" ? "reference" : "floating" : h], w = St(await r.getClippingRect({
    element: (i = await (r.isElement == null ? void 0 : r.isElement(g))) == null || i ? g : g.contextElement || await (r.getDocumentElement == null ? void 0 : r.getDocumentElement(l.floating)),
    boundary: c,
    rootBoundary: f,
    strategy: a
  })), y = h === "floating" ? {
    x: n,
    y: o,
    width: s.floating.width,
    height: s.floating.height
  } : s.reference, v = await (r.getOffsetParent == null ? void 0 : r.getOffsetParent(l.floating)), $ = await (r.isElement == null ? void 0 : r.isElement(v)) ? await (r.getScale == null ? void 0 : r.getScale(v)) || {
    x: 1,
    y: 1
  } : {
    x: 1,
    y: 1
  }, b = St(r.convertOffsetParentRelativeRectToViewportRelativeRect ? await r.convertOffsetParentRelativeRectToViewportRelativeRect({
    elements: l,
    rect: y,
    offsetParent: v,
    strategy: a
  }) : y);
  return {
    top: (w.top - b.top + u.top) / $.y,
    bottom: (b.bottom - w.bottom + u.bottom) / $.y,
    left: (w.left - b.left + u.left) / $.x,
    right: (b.right - w.right + u.right) / $.x
  };
}
const Ai = (e) => ({
  name: "arrow",
  options: e,
  async fn(t) {
    const {
      x: i,
      y: n,
      placement: o,
      rects: r,
      platform: s,
      elements: l,
      middlewareData: a
    } = t, {
      element: c,
      padding: f = 0
    } = wt(e, t) || {};
    if (c == null)
      return {};
    const h = be(f), p = {
      x: i,
      y: n
    }, d = Wt(o), u = Ft(d), m = await s.getDimensions(c), g = d === "y", w = g ? "top" : "left", y = g ? "bottom" : "right", v = g ? "clientHeight" : "clientWidth", $ = r.reference[u] + r.reference[d] - p[d] - r.floating[u], b = p[d] - r.reference[d], L = await (s.getOffsetParent == null ? void 0 : s.getOffsetParent(c));
    let E = L ? L[v] : 0;
    (!E || !await (s.isElement == null ? void 0 : s.isElement(L))) && (E = l.floating[v] || r.floating[u]);
    const rt = $ / 2 - b / 2, B = E / 2 - m[u] / 2 - 1, H = et(h[w], B), st = et(h[y], B), j = H, at = E - m[u] - st, A = E / 2 - m[u] / 2 + rt, F = Mt(j, A, at), M = !a.arrow && gt(o) != null && A !== F && r.reference[u] / 2 - (A < j ? H : st) - m[u] / 2 < 0, S = M ? A < j ? A - j : A - at : 0;
    return {
      [d]: p[d] + S,
      data: {
        [d]: F,
        centerOffset: A - F - S,
        ...M && {
          alignmentOffset: S
        }
      },
      reset: M
    };
  }
}), _i = function(e) {
  return e === void 0 && (e = {}), {
    name: "flip",
    options: e,
    async fn(t) {
      var i, n;
      const {
        placement: o,
        middlewareData: r,
        rects: s,
        initialPlacement: l,
        platform: a,
        elements: c
      } = t, {
        mainAxis: f = !0,
        crossAxis: h = !0,
        fallbackPlacements: p,
        fallbackStrategy: d = "bestFit",
        fallbackAxisSideDirection: u = "none",
        flipAlignment: m = !0,
        ...g
      } = wt(e, t);
      if ((i = r.arrow) != null && i.alignmentOffset)
        return {};
      const w = Y(o), y = I(l), v = Y(l) === l, $ = await (a.isRTL == null ? void 0 : a.isRTL(c.floating)), b = p || (v || !m ? [Et(l)] : wi(l)), L = u !== "none";
      !p && L && b.push(...bi(l, m, u, $));
      const E = [l, ...b], rt = await xe(t, g), B = [];
      let H = ((n = r.flip) == null ? void 0 : n.overflows) || [];
      if (f && B.push(rt[w]), h) {
        const A = mi(o, s, $);
        B.push(rt[A[0]], rt[A[1]]);
      }
      if (H = [...H, {
        placement: o,
        overflows: B
      }], !B.every((A) => A <= 0)) {
        var st, j;
        const A = (((st = r.flip) == null ? void 0 : st.index) || 0) + 1, F = E[A];
        if (F && (!(h === "alignment" ? y !== I(F) : !1) || // We leave the current main axis only if every placement on that axis
        // overflows the main axis.
        H.every((C) => I(C.placement) === y ? C.overflows[0] > 0 : !0)))
          return {
            data: {
              index: A,
              overflows: H
            },
            reset: {
              placement: F
            }
          };
        let M = (j = H.filter((S) => S.overflows[0] <= 0).sort((S, C) => S.overflows[1] - C.overflows[1])[0]) == null ? void 0 : j.placement;
        if (!M)
          switch (d) {
            case "bestFit": {
              var at;
              const S = (at = H.filter((C) => {
                if (L) {
                  const D = I(C.placement);
                  return D === y || // Create a bias to the `y` side axis due to horizontal
                  // reading directions favoring greater width.
                  D === "y";
                }
                return !0;
              }).map((C) => [C.placement, C.overflows.filter((D) => D > 0).reduce((D, Pe) => D + Pe, 0)]).sort((C, D) => C[1] - D[1])[0]) == null ? void 0 : at[0];
              S && (M = S);
              break;
            }
            case "initialPlacement":
              M = l;
              break;
          }
        if (o !== M)
          return {
            reset: {
              placement: M
            }
          };
      }
      return {};
    }
  };
}, Ei = /* @__PURE__ */ new Set(["left", "top"]);
async function Si(e, t) {
  const {
    placement: i,
    platform: n,
    elements: o
  } = e, r = await (n.isRTL == null ? void 0 : n.isRTL(o.floating)), s = Y(i), l = gt(i), a = I(i) === "y", c = Ei.has(s) ? -1 : 1, f = r && a ? -1 : 1, h = wt(t, e);
  let {
    mainAxis: p,
    crossAxis: d,
    alignmentAxis: u
  } = typeof h == "number" ? {
    mainAxis: h,
    crossAxis: 0,
    alignmentAxis: null
  } : {
    mainAxis: h.mainAxis || 0,
    crossAxis: h.crossAxis || 0,
    alignmentAxis: h.alignmentAxis
  };
  return l && typeof u == "number" && (d = l === "end" ? u * -1 : u), a ? {
    x: d * f,
    y: p * c
  } : {
    x: p * c,
    y: d * f
  };
}
const Ci = function(e) {
  return e === void 0 && (e = 0), {
    name: "offset",
    options: e,
    async fn(t) {
      var i, n;
      const {
        x: o,
        y: r,
        placement: s,
        middlewareData: l
      } = t, a = await Si(t, e);
      return s === ((i = l.offset) == null ? void 0 : i.placement) && (n = l.arrow) != null && n.alignmentOffset ? {} : {
        x: o + a.x,
        y: r + a.y,
        data: {
          ...a,
          placement: s
        }
      };
    }
  };
}, ki = function(e) {
  return e === void 0 && (e = {}), {
    name: "shift",
    options: e,
    async fn(t) {
      const {
        x: i,
        y: n,
        placement: o
      } = t, {
        mainAxis: r = !0,
        crossAxis: s = !1,
        limiter: l = {
          fn: (g) => {
            let {
              x: w,
              y
            } = g;
            return {
              x: w,
              y
            };
          }
        },
        ...a
      } = wt(e, t), c = {
        x: i,
        y: n
      }, f = await xe(t, a), h = I(Y(o)), p = ve(h);
      let d = c[p], u = c[h];
      if (r) {
        const g = p === "y" ? "top" : "left", w = p === "y" ? "bottom" : "right", y = d + f[g], v = d - f[w];
        d = Mt(y, d, v);
      }
      if (s) {
        const g = h === "y" ? "top" : "left", w = h === "y" ? "bottom" : "right", y = u + f[g], v = u - f[w];
        u = Mt(y, u, v);
      }
      const m = l.fn({
        ...t,
        [p]: d,
        [h]: u
      });
      return {
        ...m,
        data: {
          x: m.x - i,
          y: m.y - n,
          enabled: {
            [p]: r,
            [h]: s
          }
        }
      };
    }
  };
};
function Ot() {
  return typeof window < "u";
}
function ot(e) {
  return $e(e) ? (e.nodeName || "").toLowerCase() : "#document";
}
function _(e) {
  var t;
  return (e == null || (t = e.ownerDocument) == null ? void 0 : t.defaultView) || window;
}
function R(e) {
  var t;
  return (t = ($e(e) ? e.ownerDocument : e.document) || window.document) == null ? void 0 : t.documentElement;
}
function $e(e) {
  return Ot() ? e instanceof Node || e instanceof _(e).Node : !1;
}
function k(e) {
  return Ot() ? e instanceof Element || e instanceof _(e).Element : !1;
}
function T(e) {
  return Ot() ? e instanceof HTMLElement || e instanceof _(e).HTMLElement : !1;
}
function ce(e) {
  return !Ot() || typeof ShadowRoot > "u" ? !1 : e instanceof ShadowRoot || e instanceof _(e).ShadowRoot;
}
const Oi = /* @__PURE__ */ new Set(["inline", "contents"]);
function yt(e) {
  const {
    overflow: t,
    overflowX: i,
    overflowY: n,
    display: o
  } = O(e);
  return /auto|scroll|overlay|hidden|clip/.test(t + n + i) && !Oi.has(o);
}
const Pi = /* @__PURE__ */ new Set(["table", "td", "th"]);
function Ti(e) {
  return Pi.has(ot(e));
}
const Ri = [":popover-open", ":modal"];
function Pt(e) {
  return Ri.some((t) => {
    try {
      return e.matches(t);
    } catch {
      return !1;
    }
  });
}
const Li = ["transform", "translate", "scale", "rotate", "perspective"], Hi = ["transform", "translate", "scale", "rotate", "perspective", "filter"], Mi = ["paint", "layout", "strict", "content"];
function Vt(e) {
  const t = qt(), i = k(e) ? O(e) : e;
  return Li.some((n) => i[n] ? i[n] !== "none" : !1) || (i.containerType ? i.containerType !== "normal" : !1) || !t && (i.backdropFilter ? i.backdropFilter !== "none" : !1) || !t && (i.filter ? i.filter !== "none" : !1) || Hi.some((n) => (i.willChange || "").includes(n)) || Mi.some((n) => (i.contain || "").includes(n));
}
function Ui(e) {
  let t = z(e);
  for (; T(t) && !it(t); ) {
    if (Vt(t))
      return t;
    if (Pt(t))
      return null;
    t = z(t);
  }
  return null;
}
function qt() {
  return typeof CSS > "u" || !CSS.supports ? !1 : CSS.supports("-webkit-backdrop-filter", "none");
}
const Di = /* @__PURE__ */ new Set(["html", "body", "#document"]);
function it(e) {
  return Di.has(ot(e));
}
function O(e) {
  return _(e).getComputedStyle(e);
}
function Tt(e) {
  return k(e) ? {
    scrollLeft: e.scrollLeft,
    scrollTop: e.scrollTop
  } : {
    scrollLeft: e.scrollX,
    scrollTop: e.scrollY
  };
}
function z(e) {
  if (ot(e) === "html")
    return e;
  const t = (
    // Step into the shadow DOM of the parent of a slotted node.
    e.assignedSlot || // DOM Element detected.
    e.parentNode || // ShadowRoot detected.
    ce(e) && e.host || // Fallback.
    R(e)
  );
  return ce(t) ? t.host : t;
}
function Ae(e) {
  const t = z(e);
  return it(t) ? e.ownerDocument ? e.ownerDocument.body : e.body : T(t) && yt(t) ? t : Ae(t);
}
function ut(e, t, i) {
  var n;
  t === void 0 && (t = []), i === void 0 && (i = !0);
  const o = Ae(e), r = o === ((n = e.ownerDocument) == null ? void 0 : n.body), s = _(o);
  if (r) {
    const l = Dt(s);
    return t.concat(s, s.visualViewport || [], yt(o) ? o : [], l && i ? ut(l) : []);
  }
  return t.concat(o, ut(o, [], i));
}
function Dt(e) {
  return e.parent && Object.getPrototypeOf(e.parent) ? e.frameElement : null;
}
function _e(e) {
  const t = O(e);
  let i = parseFloat(t.width) || 0, n = parseFloat(t.height) || 0;
  const o = T(e), r = o ? e.offsetWidth : i, s = o ? e.offsetHeight : n, l = _t(i) !== r || _t(n) !== s;
  return l && (i = r, n = s), {
    width: i,
    height: n,
    $: l
  };
}
function Xt(e) {
  return k(e) ? e : e.contextElement;
}
function Z(e) {
  const t = Xt(e);
  if (!T(t))
    return P(1);
  const i = t.getBoundingClientRect(), {
    width: n,
    height: o,
    $: r
  } = _e(t);
  let s = (r ? _t(i.width) : i.width) / n, l = (r ? _t(i.height) : i.height) / o;
  return (!s || !Number.isFinite(s)) && (s = 1), (!l || !Number.isFinite(l)) && (l = 1), {
    x: s,
    y: l
  };
}
const Ni = /* @__PURE__ */ P(0);
function Ee(e) {
  const t = _(e);
  return !qt() || !t.visualViewport ? Ni : {
    x: t.visualViewport.offsetLeft,
    y: t.visualViewport.offsetTop
  };
}
function Ii(e, t, i) {
  return t === void 0 && (t = !1), !i || t && i !== _(e) ? !1 : t;
}
function K(e, t, i, n) {
  t === void 0 && (t = !1), i === void 0 && (i = !1);
  const o = e.getBoundingClientRect(), r = Xt(e);
  let s = P(1);
  t && (n ? k(n) && (s = Z(n)) : s = Z(e));
  const l = Ii(r, i, n) ? Ee(r) : P(0);
  let a = (o.left + l.x) / s.x, c = (o.top + l.y) / s.y, f = o.width / s.x, h = o.height / s.y;
  if (r) {
    const p = _(r), d = n && k(n) ? _(n) : n;
    let u = p, m = Dt(u);
    for (; m && n && d !== u; ) {
      const g = Z(m), w = m.getBoundingClientRect(), y = O(m), v = w.left + (m.clientLeft + parseFloat(y.paddingLeft)) * g.x, $ = w.top + (m.clientTop + parseFloat(y.paddingTop)) * g.y;
      a *= g.x, c *= g.y, f *= g.x, h *= g.y, a += v, c += $, u = _(m), m = Dt(u);
    }
  }
  return St({
    width: f,
    height: h,
    x: a,
    y: c
  });
}
function Rt(e, t) {
  const i = Tt(e).scrollLeft;
  return t ? t.left + i : K(R(e)).left + i;
}
function Se(e, t) {
  const i = e.getBoundingClientRect(), n = i.left + t.scrollLeft - Rt(e, i), o = i.top + t.scrollTop;
  return {
    x: n,
    y: o
  };
}
function zi(e) {
  let {
    elements: t,
    rect: i,
    offsetParent: n,
    strategy: o
  } = e;
  const r = o === "fixed", s = R(n), l = t ? Pt(t.floating) : !1;
  if (n === s || l && r)
    return i;
  let a = {
    scrollLeft: 0,
    scrollTop: 0
  }, c = P(1);
  const f = P(0), h = T(n);
  if ((h || !h && !r) && ((ot(n) !== "body" || yt(s)) && (a = Tt(n)), T(n))) {
    const d = K(n);
    c = Z(n), f.x = d.x + n.clientLeft, f.y = d.y + n.clientTop;
  }
  const p = s && !h && !r ? Se(s, a) : P(0);
  return {
    width: i.width * c.x,
    height: i.height * c.y,
    x: i.x * c.x - a.scrollLeft * c.x + f.x + p.x,
    y: i.y * c.y - a.scrollTop * c.y + f.y + p.y
  };
}
function Bi(e) {
  return Array.from(e.getClientRects());
}
function ji(e) {
  const t = R(e), i = Tt(e), n = e.ownerDocument.body, o = q(t.scrollWidth, t.clientWidth, n.scrollWidth, n.clientWidth), r = q(t.scrollHeight, t.clientHeight, n.scrollHeight, n.clientHeight);
  let s = -i.scrollLeft + Rt(e);
  const l = -i.scrollTop;
  return O(n).direction === "rtl" && (s += q(t.clientWidth, n.clientWidth) - o), {
    width: o,
    height: r,
    x: s,
    y: l
  };
}
const he = 25;
function Fi(e, t) {
  const i = _(e), n = R(e), o = i.visualViewport;
  let r = n.clientWidth, s = n.clientHeight, l = 0, a = 0;
  if (o) {
    r = o.width, s = o.height;
    const f = qt();
    (!f || f && t === "fixed") && (l = o.offsetLeft, a = o.offsetTop);
  }
  const c = Rt(n);
  if (c <= 0) {
    const f = n.ownerDocument, h = f.body, p = getComputedStyle(h), d = f.compatMode === "CSS1Compat" && parseFloat(p.marginLeft) + parseFloat(p.marginRight) || 0, u = Math.abs(n.clientWidth - h.clientWidth - d);
    u <= he && (r -= u);
  } else c <= he && (r += c);
  return {
    width: r,
    height: s,
    x: l,
    y: a
  };
}
const Wi = /* @__PURE__ */ new Set(["absolute", "fixed"]);
function Vi(e, t) {
  const i = K(e, !0, t === "fixed"), n = i.top + e.clientTop, o = i.left + e.clientLeft, r = T(e) ? Z(e) : P(1), s = e.clientWidth * r.x, l = e.clientHeight * r.y, a = o * r.x, c = n * r.y;
  return {
    width: s,
    height: l,
    x: a,
    y: c
  };
}
function de(e, t, i) {
  let n;
  if (t === "viewport")
    n = Fi(e, i);
  else if (t === "document")
    n = ji(R(e));
  else if (k(t))
    n = Vi(t, i);
  else {
    const o = Ee(e);
    n = {
      x: t.x - o.x,
      y: t.y - o.y,
      width: t.width,
      height: t.height
    };
  }
  return St(n);
}
function Ce(e, t) {
  const i = z(e);
  return i === t || !k(i) || it(i) ? !1 : O(i).position === "fixed" || Ce(i, t);
}
function qi(e, t) {
  const i = t.get(e);
  if (i)
    return i;
  let n = ut(e, [], !1).filter((l) => k(l) && ot(l) !== "body"), o = null;
  const r = O(e).position === "fixed";
  let s = r ? z(e) : e;
  for (; k(s) && !it(s); ) {
    const l = O(s), a = Vt(s);
    !a && l.position === "fixed" && (o = null), (r ? !a && !o : !a && l.position === "static" && !!o && Wi.has(o.position) || yt(s) && !a && Ce(e, s)) ? n = n.filter((f) => f !== s) : o = l, s = z(s);
  }
  return t.set(e, n), n;
}
function Xi(e) {
  let {
    element: t,
    boundary: i,
    rootBoundary: n,
    strategy: o
  } = e;
  const s = [...i === "clippingAncestors" ? Pt(t) ? [] : qi(t, this._c) : [].concat(i), n], l = s[0], a = s.reduce((c, f) => {
    const h = de(t, f, o);
    return c.top = q(h.top, c.top), c.right = et(h.right, c.right), c.bottom = et(h.bottom, c.bottom), c.left = q(h.left, c.left), c;
  }, de(t, l, o));
  return {
    width: a.right - a.left,
    height: a.bottom - a.top,
    x: a.left,
    y: a.top
  };
}
function Yi(e) {
  const {
    width: t,
    height: i
  } = _e(e);
  return {
    width: t,
    height: i
  };
}
function Ki(e, t, i) {
  const n = T(t), o = R(t), r = i === "fixed", s = K(e, !0, r, t);
  let l = {
    scrollLeft: 0,
    scrollTop: 0
  };
  const a = P(0);
  function c() {
    a.x = Rt(o);
  }
  if (n || !n && !r)
    if ((ot(t) !== "body" || yt(o)) && (l = Tt(t)), n) {
      const d = K(t, !0, r, t);
      a.x = d.x + t.clientLeft, a.y = d.y + t.clientTop;
    } else o && c();
  r && !n && o && c();
  const f = o && !n && !r ? Se(o, l) : P(0), h = s.left + l.scrollLeft - a.x - f.x, p = s.top + l.scrollTop - a.y - f.y;
  return {
    x: h,
    y: p,
    width: s.width,
    height: s.height
  };
}
function Ht(e) {
  return O(e).position === "static";
}
function fe(e, t) {
  if (!T(e) || O(e).position === "fixed")
    return null;
  if (t)
    return t(e);
  let i = e.offsetParent;
  return R(e) === i && (i = i.ownerDocument.body), i;
}
function ke(e, t) {
  const i = _(e);
  if (Pt(e))
    return i;
  if (!T(e)) {
    let o = z(e);
    for (; o && !it(o); ) {
      if (k(o) && !Ht(o))
        return o;
      o = z(o);
    }
    return i;
  }
  let n = fe(e, t);
  for (; n && Ti(n) && Ht(n); )
    n = fe(n, t);
  return n && it(n) && Ht(n) && !Vt(n) ? i : n || Ui(e) || i;
}
const Gi = async function(e) {
  const t = this.getOffsetParent || ke, i = this.getDimensions, n = await i(e.floating);
  return {
    reference: Ki(e.reference, await t(e.floating), e.strategy),
    floating: {
      x: 0,
      y: 0,
      width: n.width,
      height: n.height
    }
  };
};
function Ji(e) {
  return O(e).direction === "rtl";
}
const Zi = {
  convertOffsetParentRelativeRectToViewportRelativeRect: zi,
  getDocumentElement: R,
  getClippingRect: Xi,
  getOffsetParent: ke,
  getElementRects: Gi,
  getClientRects: Bi,
  getDimensions: Yi,
  getScale: Z,
  isElement: k,
  isRTL: Ji
};
function Oe(e, t) {
  return e.x === t.x && e.y === t.y && e.width === t.width && e.height === t.height;
}
function Qi(e, t) {
  let i = null, n;
  const o = R(e);
  function r() {
    var l;
    clearTimeout(n), (l = i) == null || l.disconnect(), i = null;
  }
  function s(l, a) {
    l === void 0 && (l = !1), a === void 0 && (a = 1), r();
    const c = e.getBoundingClientRect(), {
      left: f,
      top: h,
      width: p,
      height: d
    } = c;
    if (l || t(), !p || !d)
      return;
    const u = bt(h), m = bt(o.clientWidth - (f + p)), g = bt(o.clientHeight - (h + d)), w = bt(f), v = {
      rootMargin: -u + "px " + -m + "px " + -g + "px " + -w + "px",
      threshold: q(0, et(1, a)) || 1
    };
    let $ = !0;
    function b(L) {
      const E = L[0].intersectionRatio;
      if (E !== a) {
        if (!$)
          return s();
        E ? s(!1, E) : n = setTimeout(() => {
          s(!1, 1e-7);
        }, 1e3);
      }
      E === 1 && !Oe(c, e.getBoundingClientRect()) && s(), $ = !1;
    }
    try {
      i = new IntersectionObserver(b, {
        ...v,
        // Handle <iframe>s
        root: o.ownerDocument
      });
    } catch {
      i = new IntersectionObserver(b, v);
    }
    i.observe(e);
  }
  return s(!0), r;
}
function tn(e, t, i, n) {
  n === void 0 && (n = {});
  const {
    ancestorScroll: o = !0,
    ancestorResize: r = !0,
    elementResize: s = typeof ResizeObserver == "function",
    layoutShift: l = typeof IntersectionObserver == "function",
    animationFrame: a = !1
  } = n, c = Xt(e), f = o || r ? [...c ? ut(c) : [], ...ut(t)] : [];
  f.forEach((w) => {
    o && w.addEventListener("scroll", i, {
      passive: !0
    }), r && w.addEventListener("resize", i);
  });
  const h = c && l ? Qi(c, i) : null;
  let p = -1, d = null;
  s && (d = new ResizeObserver((w) => {
    let [y] = w;
    y && y.target === c && d && (d.unobserve(t), cancelAnimationFrame(p), p = requestAnimationFrame(() => {
      var v;
      (v = d) == null || v.observe(t);
    })), i();
  }), c && !a && d.observe(c), d.observe(t));
  let u, m = a ? K(e) : null;
  a && g();
  function g() {
    const w = K(e);
    m && !Oe(m, w) && i(), m = w, u = requestAnimationFrame(g);
  }
  return i(), () => {
    var w;
    f.forEach((y) => {
      o && y.removeEventListener("scroll", i), r && y.removeEventListener("resize", i);
    }), h?.(), (w = d) == null || w.disconnect(), d = null, a && cancelAnimationFrame(u);
  };
}
const en = Ci, nn = ki, on = _i, rn = Ai, sn = (e, t, i) => {
  const n = /* @__PURE__ */ new Map(), o = {
    platform: Zi,
    ...i
  }, r = {
    ...o.platform,
    _c: n
  };
  return $i(e, t, {
    ...o,
    platform: r
  });
}, an = {
  top: "bottom",
  bottom: "top",
  left: "right",
  right: "left"
};
function ln(e, t, i, n) {
  if (!e) return;
  const o = i?.offsetWidth ?? 0, r = hn(n);
  return tn(
    e,
    t,
    () => {
      sn(e, t, {
        placement: r,
        middleware: [
          nn(),
          on(),
          en(16),
          rn({ element: i })
        ]
      }).then(({ x: s, y: l, middlewareData: a, placement: c }) => {
        Object.assign(t.style, {
          left: `${s}px`,
          top: `${l}px`
        });
        const f = c.split("-")[0], h = an[f];
        if (!h) throw new Error("Invalid side");
        if (a.arrow) {
          const { x: p, y: d } = a.arrow;
          Object.assign(i.style, {
            // Use != to check for null or undefined and allow 0 values
            left: p != null ? `${p}px` : "",
            top: d != null ? `${d}px` : "",
            // Ensure the opposite side gets unset when
            // flipping to other placements' axes.
            right: "",
            bottom: "",
            // Add two pixels for smoother arrow placement
            [h]: `${(-o + 2) / 2}px`,
            transform: "rotate(45deg)"
          });
        }
      });
    },
    { animationFrame: !0 }
  );
}
const cn = {
  "above-start": "top-start",
  above: "top",
  "above-end": "top-end",
  "below-start": "bottom-start",
  below: "bottom",
  "below-end": "bottom-end",
  "before-top": "bottom-start",
  before: "left",
  "before-bottom": "left-end",
  "after-top": "right-start",
  after: "right",
  "after-bottom": "right-end"
}, hn = (e) => {
  const t = cn[e];
  if (!t)
    throw new Error(`Unsupported placement: ${e}`);
  return t;
};
var dn = Object.defineProperty, G = (e, t, i, n) => {
  for (var o = void 0, r = e.length - 1, s; r >= 0; r--)
    (s = e[r]) && (o = s(t, i, o) || o);
  return o && dn(t, i, o), o;
};
const Yt = class Yt extends ht {
  constructor() {
    super(), this.elementInternals = this.attachInternals(), this.defaultDelay = 300, this.position = "above", this.id = di("a11y-bdk-tooltip"), this.mouseenterAnchorHandler = () => this.showTooltip(this.delay), this.clickAnchorHandler = () => this.showTooltip(this.delay), this.mouseleaveAnchorHandler = () => this.hideTooltip(this.delay), this.focusAnchorHandler = () => this.showTooltip(0), this.blurAnchorHandler = () => this.hideTooltip(0), this.elementInternals.role = "tooltip";
  }
  get anchorElement() {
    if (this.previousElementSibling?.getAttribute("id") === this.anchorId)
      return this.previousElementSibling;
    const t = this.getRootNode();
    return (t instanceof ShadowRoot ? t : document).getElementById(this.anchorId ?? "");
  }
  connectedCallback() {
    if (super.connectedCallback(), !this.anchorElement)
      throw new Error("no anchor element found with id: " + this.anchorId);
    this.popover = "", this.setAttribute("aria-hidden", "true"), this.setAttribute("role", "tooltip");
    const t = this.anchorElement.getAttribute("aria-describedby");
    this.anchorElement.setAttribute(
      "aria-describedby",
      t ? `${t} ${this.id}` : this.id
    ), this.setAttribute("id", this.id), this.anchorElement.addEventListener("focus", this.focusAnchorHandler), this.anchorElement.addEventListener("blur", this.blurAnchorHandler), this.anchorElement.addEventListener(
      "mouseenter",
      this.mouseenterAnchorHandler
    ), this.anchorElement.addEventListener(
      "mouseleave",
      this.mouseleaveAnchorHandler
    ), this.addEventListener("mouseleave", this.mouseleaveAnchorHandler), this.anchorElement.addEventListener("click", this.clickAnchorHandler);
  }
  disconnectedCallback() {
    super.disconnectedCallback(), clearTimeout(this.showTimeoutId), clearTimeout(this.hideTimeoutId), this.cleanupAutoUpdate?.(), this.cleanupAutoUpdate = void 0, this.anchorElement?.removeEventListener("focus", this.focusAnchorHandler), this.anchorElement?.removeEventListener("blur", this.blurAnchorHandler), this.anchorElement?.removeEventListener(
      "mouseenter",
      this.mouseenterAnchorHandler
    ), this.anchorElement?.removeEventListener(
      "mouseleave",
      this.mouseleaveAnchorHandler
    ), this.removeEventListener("mouseleave", this.mouseleaveAnchorHandler), this.anchorElement?.removeEventListener("click", this.clickAnchorHandler);
    const t = this.anchorElement?.getAttribute("aria-describedby")?.replace(this.id, "").trim();
    t ? this.anchorElement?.setAttribute("aria-describedby", t) : this.anchorElement?.removeAttribute("aria-describedby");
  }
  showTooltip(t = this.defaultDelay) {
    this.anchorElement?.hasAttribute("disabled") || (clearTimeout(this.hideTimeoutId), this.showTimeoutId = setTimeout(() => {
      this.setAttribute("aria-hidden", "false"), this.showPopover(), this.cleanupAutoUpdate = ln(
        this.anchorElement,
        this,
        this.tooltipArrow,
        this.position
      );
    }, t));
  }
  hideTooltip(t = this.defaultDelay) {
    this.matches(":hover") || this.anchorElement?.matches(":hover") || document.activeElement === this.anchorElement || (clearTimeout(this.showTimeoutId), this.hideTimeoutId = setTimeout(() => {
      this.cleanupAutoUpdate?.(), this.cleanupAutoUpdate = void 0, this.setAttribute("aria-hidden", "true"), this.hidePopover();
    }, t));
  }
  render() {
    return vt`
      <div aria-hidden="true" class="tooltip--arrow"></div>
      <div class="tooltip--content">
        ${this.iconHTML()} ${this.headerHTML()} ${this.contentHTML()}
      </div>
    `;
  }
  iconHTML() {
    return this.hasSlotContent("icon") ? vt`<div class="tooltip--icon">
      <slot name="icon"></slot>
    </div>` : null;
  }
  headerHTML() {
    if (this.headline)
      return vt`<div class="tooltip--header">${this.headline}</div>`;
  }
  contentHTML() {
    if (this.content)
      return vt`<div class="tooltip--text">${this.content}</div>`;
  }
  hasSlotContent(t) {
    return !!this.querySelector(`[slot="${t}"]`);
  }
};
Yt.styles = ue(ai);
let U = Yt;
G([
  nt({ type: String, attribute: "anchor-id" })
], U.prototype, "anchorId");
G([
  nt({ type: String })
], U.prototype, "content");
G([
  nt({ type: String, reflect: !0 })
], U.prototype, "position");
G([
  nt({ type: String })
], U.prototype, "headline");
G([
  nt({ type: Number })
], U.prototype, "delay");
G([
  nt({ type: String, reflect: !0 })
], U.prototype, "id");
G([
  si(".tooltip--arrow")
], U.prototype, "tooltipArrow");
export {
  U as BdkTooltip
};
